/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQuery } from 'react-query';
import { setAccessToken } from '../axios';
import { partnerSentryConfig } from './sentry';
import {
  partnerLogin,
  partnerRegister,
  registerCompany,
  registerDiscount,
  getCompanyDiscounts,
  IPartnerUserRegistrationData,
  IPartnerLoginResponse,
  ICompanyRegistrationData,
  ICompanyResponse,
  IDiscountData,
  IDiscountResponse,
  getCompany,
  IStoreData,
} from './api';

export function useGetCompanyDiscounts() {
  return useQuery(['company-discounts'], () => getCompanyDiscounts(), {
    retry: false,
    refetchOnWindowFocus: false,
    onError: (error: any) => {
      partnerSentryConfig.captureApiError(error, {
        hook: 'useGetCompanyDiscounts',
        query: 'company-discounts',
      });
    },
  });
}

export function useGetCompany() {
  return useQuery(['company'], () => getCompany(), {
    retry: false,
    refetchOnWindowFocus: false,
    onError: (error: any) => {
      partnerSentryConfig.captureApiError(error, {
        hook: 'useGetCompany',
        query: 'company',
      });
    },
  });
}

// Hook para registrar empresa
export function useRegisterCompany({
  onSuccess,
  onError,
}: {
  onSuccess: (data: { store: IStoreData; company: ICompanyResponse }) => void;
  onError: (error: any) => void;
}) {
  return useMutation(
    ({ data, agreementId }: { data: ICompanyRegistrationData; agreementId: string }) =>
      registerCompany(data, agreementId),
    {
      onSuccess: function (data) {
        console.log('data', data);
        onSuccess(data);
      },
      onError: function (error: any) {
        // Capturar erro no Sentry
        partnerSentryConfig.captureApiError(error, {
          hook: 'useRegisterCompany',
          mutation: 'registerCompany',
        });

        const response = error.response as {
          data: {
            error?: string;
            details?: any;
            message?: string;
            cnpj?: string[];
            name?: string[];
            corporate_name?: string[];
          };
        };

        let errorMessage = 'Erro ao registrar empresa';

        if(response.data.details){
          return onError(response.data);
        }else if (response?.data?.error) {
          errorMessage = response.data.error;
        } else if (response?.data?.message) {
          errorMessage = response.data.message;
        } else if (response?.data?.cnpj) {
          errorMessage = `CNPJ: ${response.data.cnpj.join(', ')}`;
        } else if (response?.data?.name) {
          errorMessage = `Nome: ${response.data.name.join(', ')}`;
        } else if (response?.data?.corporate_name) {
          errorMessage = `Razão Social: ${response.data.corporate_name.join(', ')}`;
        }

        onError(errorMessage);
      },
    }
  );
}

// Hook para registrar desconto
export function useRegisterDiscount({
  onSuccess,
  onError,
}: {
  onSuccess: (data: IDiscountResponse) => void;
  onError: (error: string) => void;
}) {
  return useMutation((data: IDiscountData) => registerDiscount(data), {
    onSuccess: function (data) {
      onSuccess(data);
    },
    onError: function (error: any) {
      // Capturar erro no Sentry
      partnerSentryConfig.captureApiError(error, {
        hook: 'useRegisterDiscount',
        mutation: 'registerDiscount',
      });

      const response = error.response as {
        data: {
          error?: string;
          message?: string;
          discount?: string[];
          title_discount?: string[];
        };
      };

      let errorMessage = 'Erro ao registrar desconto';

      if (response?.data?.error) {
        errorMessage = response.data.error;
      } else if (response?.data?.message) {
        errorMessage = response.data.message;
      } else if (response?.data?.discount) {
        errorMessage = `Desconto: ${response.data.discount.join(', ')}`;
      } else if (response?.data?.title_discount) {
        errorMessage = `Título: ${response.data.title_discount.join(', ')}`;
      }

      onError(errorMessage);
    },
  });
}

// Hook para login de parceiro
export function usePartnerLogin({
  onSuccess,
  onError,
}: {
  onSuccess: (data: IPartnerLoginResponse) => void;
  onError: (error: string) => void;
}) {
  return useMutation(({ email, password }: { email: string; password: string }) => partnerLogin({ email, password }), {
    onSuccess: function (data) {
      setAccessToken(data.token);
      onSuccess(data);
    },
    onError: function (error: any) {
      // Capturar erro no Sentry
      partnerSentryConfig.captureApiError(error, {
        hook: 'usePartnerLogin',
        mutation: 'partnerLogin',
      });

      const response = error.response as { data: { error?: string; message?: string } };
      const errorMessage = response?.data?.error || response?.data?.message || 'Erro ao fazer login';
      onError(errorMessage);
    },
  });
}

// Hook para registro de parceiro (apenas usuário)
export function usePartnerRegister({
  onSuccess,
  onError,
}: {
  onSuccess: (data: IPartnerLoginResponse) => void;
  onError: (error: string) => void;
}) {
  return useMutation(
    ({ data, agreementId }: { data: IPartnerUserRegistrationData; agreementId: string }) =>
      partnerRegister(data, agreementId),
    {
      onSuccess: function (data) {
        setAccessToken(data.token);
        onSuccess(data);
      },
          onError: function (error: any) {
      // Capturar erro no Sentry
      partnerSentryConfig.captureApiError(error, {
        hook: 'usePartnerRegister',
        mutation: 'partnerRegister',
      });

      const response = error.response as { data: { error?: string; message?: string } };
      const errorMessage = response?.data?.error || response?.data?.message || 'Erro ao criar conta';
      onError(errorMessage);
    },
    }
  );
}
